import React from 'react';
import AdminLayout from '../../../components/admin/AdminLayout';
import pb from '../../../lib/pocketbase';

const emptyForm = {
  name: '',
  slug: '',
  sort_order: 0,
  is_active: true,
};

function makeSlug(input) {
  return String(input || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');
}

function AdminSiteBlogCategoriesPage() {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const dragIndexRef = React.useRef(-1);

  const [items, setItems] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [form, setForm] = React.useState(emptyForm);
  const [slugTouched, setSlugTouched] = React.useState(false);

  const fetchItems = async () => {
    setError('');
    try {
      const res = await pb.collection('site_blog_categories').getList(1, 200, { sort: 'sort_order, -created' });
      setItems(res?.items ?? []);
    } catch (e) {
      setError(e?.message || 'Erreur chargement des catégories');
      setItems([]);
    }
  };

  React.useEffect(() => {
    fetchItems();
  }, []);

  const persistOrder = async (orderedItems) => {
    try {
      await Promise.all(
        orderedItems.map((rec, idx) => pb.collection('site_blog_categories').update(rec.id, { sort_order: idx + 1 }))
      );
    } catch (e) {
      alert(e?.message || 'Erreur de ré-ordre');
    }
  };

  const onDragStart = (e, index) => {
    dragIndexRef.current = index;
    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = (e, overIndex) => {
    e.preventDefault();
    const from = dragIndexRef.current;
    if (from === -1 || from === overIndex) return;
    setItems((arr) => {
      const next = arr.slice();
      const [moved] = next.splice(from, 1);
      next.splice(overIndex, 0, moved);
      dragIndexRef.current = overIndex;
      return next;
    });
  };

  const onDrop = async () => {
    dragIndexRef.current = -1;
    const ordered = [...items];
    await persistOrder(ordered);
    await fetchItems();
  };

  const openCreate = () => {
    setEditing(null);
    setSlugTouched(false);
    setForm({ ...emptyForm, sort_order: items.length ? Math.max(...items.map(i => Number(i.sort_order || 0))) + 1 : 0 });
    setOpen(true);
  };

  const openEdit = (it) => {
    setEditing(it);
    setSlugTouched(true);
    setForm({
      name: it?.name || '',
      slug: it?.slug || '',
      sort_order: Number(it?.sort_order || 0),
      is_active: it?.is_active !== false,
    });
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
    setEditing(null);
    setForm(emptyForm);
    setSlugTouched(false);
  };

  const handleNameChange = (value) => {
    setForm((f) => {
      const next = { ...f, name: value };
      if (!slugTouched) next.slug = makeSlug(value);
      return next;
    });
  };

  const handleSlugChange = (value) => {
    setSlugTouched(true);
    setForm((f) => ({ ...f, slug: value }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = {
        name: form.name || '',
        slug: form.slug || '',
        sort_order: Number(form.sort_order || 0),
        is_active: !!form.is_active,
      };

      if (editing?.id) {
        await pb.collection('site_blog_categories').update(editing.id, payload);
      } else {
        await pb.collection('site_blog_categories').create(payload);
      }
      await fetchItems();
      closeModal();
    } catch (e) {
      alert(e?.message || "Erreur d'enregistrement");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette catégorie ?')) return;
    try {
      await pb.collection('site_blog_categories').delete(id);
      await fetchItems();
    } catch (e) {
      alert(e?.message || 'Erreur lors de la suppression');
    }
  };

  const handleToggleActive = async (it) => {
    try {
      await pb.collection('site_blog_categories').update(it.id, { is_active: !(it?.is_active !== false) });
      await fetchItems();
    } catch (e) {
      alert(e?.message || 'Erreur lors de la mise à jour');
    }
  };

  return (
    <AdminLayout title="">
      <h1 style={{ marginTop: 0 }}>Blog · Catégories</h1>
      <p>Gérez les catégories des articles du blog.</p>

      <style>{`
        .btn-primary:hover { transform: translateY(-1px); filter: brightness(1.04); }
        .btn-primary:active { transform: translateY(0); filter: brightness(0.98); }
        .toggle-pill{ cursor:pointer; border:1px solid var(--admin-border); background: var(--admin-panel); color: var(--admin-text); padding:6px 10px; border-radius:999px; font-size:.85rem; }
        .toggle-pill.on{ border-color: rgba(34,197,94,0.55); box-shadow: 0 0 0 3px rgba(34,197,94,0.15); }
        .toggle-pill.off{ opacity:.8; }
        .grid{ display:grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 12px; }
        @media (max-width: 480px){ .grid{ grid-template-columns: 1fr; } }

        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1001; padding: 20px; }
        .modal-content { background: var(--admin-panel); color: var(--admin-text); border: 1px solid var(--admin-border); border-radius: 12px; width: 100%; max-width: 720px; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.35); }
        .modal-header { display: flex; justify-content: space-between; align-items: center; padding: 20px; border-bottom: 1px solid var(--admin-border); }
        .modal-body{ padding: 16px 20px; display:grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .modal-body .full{ grid-column: 1 / -1; }
        .close-btn { background: none; border: 1px solid transparent; color: var(--admin-muted); cursor: pointer; padding: 8px; border-radius: 6px; }
        .close-btn:hover { background: rgba(255,255,255,0.06); border-color: var(--admin-border); color: var(--admin-text); }
        .modal-actions { display:flex; gap: 12px; justify-content:flex-end; padding: 16px 20px; border-top:1px solid var(--admin-border); }

        .field{ display:flex; flex-direction:column; gap: 6px; }
        .field label{ font-size: .9rem; color: var(--admin-muted); }
        .field input[type="text"], .field input[type="number"]{
          background: var(--admin-panel-2);
          border:1px solid var(--admin-border);
          color: var(--admin-text);
          border-radius: 10px;
          padding: 10px 10px;
          outline:none;
        }
        .field input:focus{ border-color: var(--admin-accent); box-shadow: 0 0 0 3px rgba(79,70,229,0.20); }
        .field small{ color: var(--admin-muted); }
        .btn{ cursor:pointer; border:1px solid var(--admin-border); background: transparent; color: var(--admin-text); padding: 10px 12px; border-radius: 10px; }
        .btn:hover{ background: rgba(255,255,255,0.06); }
        .admin-shell.admin-light .btn:hover{ background: rgba(2,6,23,0.06); }
        .btn-solid{ border-color: rgba(79,70,229,0.6); background: linear-gradient(135deg, var(--admin-accent), rgba(79,70,229,0.85)); color:#fff; }
        .btn-solid:disabled{ opacity: .6; cursor: not-allowed; }
        .btn-danger{ border-color: rgba(239,68,68,0.5); }
      `}</style>

      {error && <div style={{ color: '#ef4444', marginBottom: 8 }}>{error}</div>}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <div style={{ color: 'var(--admin-muted)', fontSize: '.9rem' }}>Utilisées pour filtrer les articles.</div>
        <button className="btn btn-solid btn-primary" onClick={openCreate}>Nouvelle catégorie</button>
      </div>

      <div className="grid">
        {items.map((it, idx) => (
          <div
            key={it.id}
            className="card"
            draggable
            onDragStart={(e) => onDragStart(e, idx)}
            onDragOver={(e) => onDragOver(e, idx)}
            onDrop={(e) => {
              e.preventDefault();
              onDrop();
            }}
            title={`Glissez pour réordonner (position ${idx + 1})`}
            style={{ cursor: 'grab' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
              <div>
                <div style={{ fontWeight: 800, lineHeight: 1.2 }}>{it.name || '(Sans nom)'}</div>
                <div style={{ color: 'var(--admin-muted)', fontSize: '.9rem', marginTop: 4 }}>slug: {it.slug || '-'}</div>
                <div style={{ color: 'var(--admin-muted)', fontSize: '.85rem', marginTop: 6 }}>ordre: {idx + 1}</div>
              </div>
              <button className={`toggle-pill ${(it?.is_active !== false) ? 'on' : 'off'}`} onClick={() => handleToggleActive(it)}>
                {(it?.is_active !== false) ? 'Actif' : 'Inactif'}
              </button>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              <button className="btn" onClick={() => openEdit(it)}>Modifier</button>
              <button className="btn btn-danger" onClick={() => handleDelete(it.id)}>Supprimer</button>
            </div>
          </div>
        ))}
      </div>

      {open && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onMouseDown={(e) => {
          if (e.target?.classList?.contains('modal-overlay')) closeModal();
        }}>
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editing?.id ? 'Modifier la catégorie' : 'Nouvelle catégorie'}</h3>
              <button className="close-btn" onClick={closeModal} aria-label="Fermer">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="field full">
                <label>Nom</label>
                <input type="text" value={form.name} onChange={(e) => handleNameChange(e.target.value)} />
              </div>
              <div className="field">
                <label>Slug</label>
                <input type="text" value={form.slug} onChange={(e) => handleSlugChange(e.target.value)} placeholder="ex: seo" />
                <small>Doit être unique</small>
              </div>
              <div className="field">
                <label>Ordre (sort_order)</label>
                <input type="number" value={form.sort_order} onChange={(e) => setForm(f => ({ ...f, sort_order: e.target.value }))} />
              </div>
              <div className="field">
                <label>Actif</label>
                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm(f => ({ ...f, is_active: e.target.checked }))} />
              </div>
            </div>
            <div className="modal-actions">
              {editing?.id && (
                <button className="btn btn-danger" onClick={() => { closeModal(); handleDelete(editing.id); }}>Supprimer</button>
              )}
              <button className="btn" onClick={closeModal}>Annuler</button>
              <button className="btn btn-solid" onClick={handleSave} disabled={loading}>
                {loading ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default AdminSiteBlogCategoriesPage;
